/**
 * AI Product Mapping — the run slice's product-first group reducers.
 *
 * Split out of `./runSlice` to keep that file inside the size budget; spread into the
 * slice's `reducers` so the action names and `runActions.*` call sites are unchanged.
 *
 * Two invariants live here:
 *  - verdicts are UPSERTS by groupKey (the funnel re-emits a group as stages firm it up),
 *  - a decision that already landed is never walked back by a later verdict re-emission.
 */

import { blankRow, makeAiReview } from './runSlice';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RunGroupAiSuggestion, RunGroupDecisionState, RunGroupItem } from './api/groupTypes';
import type { ProductMappingRunState } from './runSlice';

/** A group's decision transition, as carried by a `group_state` frame. */
export interface GroupStatePatch {
  groupKey: string;
  decisionState: RunGroupDecisionState;
  chosenProductIds: string[];
  decisionSource: string | null;
}

// A frame that makes no decision claim (decision fields omitted) never walks back a
// decision the client already holds — that guards a REST decision against stale
// in-flight re-emissions. A frame that STATES a decision (decisionStated, including an
// explicit "pending" on a server revert) is authoritative — except over a reviewer-held
// decision, which the server never legitimately reverts by frame.
function upsertGroup(state: ProductMappingRunState, incoming: RunGroupItem): void {
  const existing = state.groups[incoming.groupKey];
  if (!existing) {
    state.groups[incoming.groupKey] = incoming;
    return;
  }
  const keepDecision =
    existing.decisionState !== 'pending' &&
    (!incoming.decisionStated || existing.decisionSource === 'reviewer');
  state.groups[incoming.groupKey] = {
    ...existing,
    ...incoming,
    decisionState: keepDecision ? existing.decisionState : incoming.decisionState,
    chosenProductIds: keepDecision ? existing.chosenProductIds : incoming.chosenProductIds,
    decisionSource: keepDecision ? existing.decisionSource : incoming.decisionSource,
    // The restore read (or an expand's row fetch) carries every member index; frames
    // carry none. Never let the leaner frame shape erase the richer one.
    memberLineIdxs: incoming.memberLineIdxs ?? existing.memberLineIdxs,
  };
}

export const groupReducers = {
  /** One `group_verdict` frame (kept for direct dispatches; the stream batches). */
  groupVerdictReceived: (state: ProductMappingRunState, { payload }: PayloadAction<RunGroupItem>) => {
    upsertGroup(state, payload);
  },

  /** A flushed batch of `group_verdict` frames — one dispatch per flush window instead of
   *  one per group, which matters on files with hundreds of groups. */
  groupVerdictsBatched: (state: ProductMappingRunState, { payload }: PayloadAction<RunGroupItem[]>) => {
    for (const group of payload) upsertGroup(state, group);
  },

  /** A group's decision transition (auto-confirm mid-stream, or replayed on reconnect).
   *  Groups arrive verdict-first; a state frame for an unknown key is parked as a minimal
   *  skeleton so the transition is never dropped on a reordered replay. */
  groupStateChanged: (state: ProductMappingRunState, { payload }: PayloadAction<GroupStatePatch>) => {
    const existing = state.groups[payload.groupKey];
    if (existing) {
      existing.decisionState = payload.decisionState;
      existing.chosenProductIds = payload.chosenProductIds;
      existing.decisionSource = payload.decisionSource;
      return;
    }
    state.groups[payload.groupKey] = {
      groupKey: payload.groupKey,
      label: payload.groupKey,
      memberCount: 0,
      sampleCount: 0,
      verdict: 'pending',
      autoConfirmed: false,
      candidates: [],
      decisionState: payload.decisionState,
      chosenProductIds: payload.chosenProductIds,
      decisionSource: payload.decisionSource,
    };
  },

  /** Restore path: one shot of the run's stored groups (GET .../groups). */
  groupsLoaded: (state: ProductMappingRunState, { payload }: PayloadAction<RunGroupItem[]>) => {
    state.groups = {};
    for (const group of payload) state.groups[group.groupKey] = group;
  },

  /** A decision response's authoritative group snapshot (rows merge via rowsMatched). */
  groupDecided: (state: ProductMappingRunState, { payload }: PayloadAction<RunGroupItem>) => {
    state.groups[payload.groupKey] = payload;
  },

  /** One group's on-demand row fetch answered with the authoritative membership — frames
   *  carry only the anchor, so this is what gives a live group its grid slots. The rows
   *  themselves merge through rowsMatched (same dispatch batch in the thunk). */
  groupMembersKnown: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ groupKey: string; memberLineIdxs: number[] }>,
  ) => {
    const group = state.groups[payload.groupKey];
    if (group && payload.memberLineIdxs.length) group.memberLineIdxs = payload.memberLineIdxs;
  },

  /** A decided group's member rows leave the grid (back to placeholders): the lean
   *  decision response echoes no rows, so what is showing is stale — and re-fetching it
   *  eagerly would pay the payload for data the reviewer may never look at again. The
   *  group also collapses (page-side); the next expand re-fetches fresh rows. */
  groupRowsDropped: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ lineIdxs: number[] }>,
  ) => {
    for (const lineIdx of payload.lineIdxs) {
      if (state.rows[lineIdx]) state.rows[lineIdx] = blankRow(lineIdx + 1);
    }
  },

  /** One group's member-row fetch lifecycle (expand / post-decision re-expand). */
  groupRowsLoadStarted: (state: ProductMappingRunState, { payload }: PayloadAction<{ groupKey: string }>) => {
    state.groupRowsLoading[payload.groupKey] = true;
  },

  groupRowsLoadFinished: (state: ProductMappingRunState, { payload }: PayloadAction<{ groupKey: string }>) => {
    delete state.groupRowsLoading[payload.groupKey];
  },

  /** The restore's group page-walk lifecycle — started before the first request, a
   *  progress tick per page, cleared in the walk's finally (success or failure). */
  groupsLoadStarted: (state: ProductMappingRunState) => {
    state.groupsLoading = { received: 0, total: 0 };
  },

  groupsLoadProgress: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ received: number; total: number }>,
  ) => {
    state.groupsLoading = payload;
  },

  groupsLoadFinished: (state: ProductMappingRunState) => {
    state.groupsLoading = null;
  },

  // --- The on-demand AI review (advisory suggestions over pending groups) ---

  /** The reviewer clicked "Run AI review" — running from the click, so the thinking chips
   *  paint before the server's started frame lands. */
  aiReviewRequested: (state: ProductMappingRunState) => {
    state.aiReview = { ...makeAiReview(), status: 'running' };
  },

  /** ai_review_started — the judged total (after the server's per-run cap). */
  aiReviewStarted: (state: ProductMappingRunState, { payload }: PayloadAction<{ total: number }>) => {
    state.aiReview.status = 'running';
    state.aiReview.attempted = 0;
    state.aiReview.total = payload.total;
  },

  aiReviewProgress: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ attempted: number; total: number }>,
  ) => {
    // Batches land out of order server-side too — only move forward.
    if (payload.attempted > state.aiReview.attempted) state.aiReview.attempted = payload.attempted;
    if (payload.total > 0) state.aiReview.total = payload.total;
  },

  /** One group_ai_suggestion frame. A suggestion never lands on a decided group — the
   *  reviewer's answer beat the AI's, and the server skipped its write for the same race. */
  aiReviewSuggestionReceived: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ groupKey: string } & RunGroupAiSuggestion>,
  ) => {
    const group = state.groups[payload.groupKey];
    if (!group || group.decisionState !== 'pending') return;
    group.aiSuggestion = {
      verdict: payload.verdict,
      productIds: payload.productIds,
      productTitles: payload.productTitles ?? null,
      confidence: payload.confidence,
      reason: payload.reason ?? null,
    };
  },

  /** ai_review_done (or a dropped stream) — tallies, or the error that ended it. */
  aiReviewFinished: (
    state: ProductMappingRunState,
    { payload }: PayloadAction<{ suggested?: number; none?: number; unanswered?: number; error?: string | null }>,
  ) => {
    state.aiReview.status = payload.error ? 'failed' : 'done';
    state.aiReview.suggested = payload.suggested ?? state.aiReview.suggested;
    state.aiReview.none = payload.none ?? state.aiReview.none;
    state.aiReview.unanswered = payload.unanswered ?? state.aiReview.unanswered;
    state.aiReview.error = payload.error ?? null;
  },
};
